/*
	Fallback auth flow mechanism for authenticating with Google
*/

// Imports ------------------------------------------------------------------->

import {inject} from "../di";
import {ConfigService, RuntimeConfig} from "../ConfigService";
import {AccountsService} from "../AccountsService";
import {AnalyticsService} from "../AnalyticsService";
import {LocationService} from "../LocationService";
import {IAuthenticationService, AuthUser, AuthProviderUser, AuthStatus} from "../definitions/IAuthenticationService";
import {AuthProvider, AccountsUser} from "../../definitions/CommonDefinitions";
import {fetchJSON} from "../../utils/fetch";
import {parseHash} from "../../utils/URL";
import {generateUUID} from "../../utils/uuid";

// Private Definitions ------------------------------------------------------->

const OAUTH_URL = "https://accounts.google.com/o/oauth2/v2/auth";
const VALIDATION_URL = "https://www.googleapis.com/oauth2/v3/tokeninfo";
const PROFILE_URL = "https://www.googleapis.com/plus/v1/people/me";

// Public Definitions -------------------------------------------------------->

export class GoogleRedirectAuthenticator implements IAuthenticationService {

	@inject accountsService: AccountsService;
	@inject config: ConfigService;
	@inject analytics: AnalyticsService;
	@inject location: LocationService;

	get status() {
		const loginCookie = Cookies.getJSON("flipp-user");
		return loginCookie ? AuthStatus.LOGGED_IN : AuthStatus.LOGGED_OUT;
	}

	get user() {
		const loginCookie = Cookies.get("flipp-user");
		return {
			info: JSON.parse(loginCookie)
		} as AuthUser;
	}

	init = () => {
		return new Promise<void>((resolve, reject) => {
			if (window.location.pathname !== "/login_redirect") {
				resolve();
				return;
			}

			const url = new URL(window.location.href);
			const params = parseHash(url);

			let provider = -1;
			try {
				const state = JSON.parse(params["state"]);
				provider = state.provider;
			} catch (e) {}

			/* init() is called as by the Router as a global middleware. We can check the URL for a hash
				* and identify if id_token and access_token are provided. If they are, we know we are in the
				* middle of the oauth flow and can parse out the values. */
			if (provider === AuthProvider.GOOGLE && params["id_token"] && params["access_token"]) {
				const profileUrl = `${PROFILE_URL}?access_token=${params["access_token"]}`;
				const validation = `${VALIDATION_URL}?access_token=${params["access_token"]}`;

				// Construct a fake AuthProviderUser - we don't have name, email or image yet, but we only
				// need the accessToken and provider to fetch a token from Accounts API
				const user = {
					id: "",
					name: "",
					email: "",
					image: "",
					accessToken: params["id_token"],
					provider: AuthProvider.GOOGLE
				} as AuthProviderUser;

				/* Call three different endpoints here:
					*  1) Profile endpoint on Google to get name/email/image
					*  2) fetchToken endpoint on Accounts API to get a token
					*  3) Validation endpoint on Google to verify the provided access token is correct
					*      See: https://developers.google.com/identity/protocols/OAuth2UserAgent#validatetoken
					*/
				Promise.all<any, AccountsUser, any>([
					fetchJSON(profileUrl),
					this.accountsService.fetchToken(user),
					fetchJSON(validation)
				]).then(([profile, accountsUser, validation]: [any, any, any]) => {

					if ("error" in validation) {
						reject();
						return;
					}

					/* Updated the accounts info with the profile details*/
					user.id = profile.id;
					user.name = profile.displayName;
					user.image = profile.image ? profile.image.url : "";
					user.email = accountsUser.email;

					/* Save two cookies:
						* 1) flipp-user: containing the user specific (ie AuthProviderUser interface)
						* 2) flipp-login: login info from Accounts API
						*/
					Cookies.set("flipp-user", JSON.stringify(user), {
						expires: 30,
						// domain: this.config.getCookiesDomain()
					});
					Cookies.set("flipp-login", JSON.stringify({token: accountsUser}), {
						expires: 30,
						// domain: this.config.getCookiesDomain()
					});
					resolve();
				}).catch((err: Error) => {
					reject(err);
				});
			} else {
				resolve();
			}
		});
	}

	login = () => {
		return Promise.resolve().then(() => {
			const uuid = window.btoa(generateUUID());
			const redirectUrl = `${window.location.protocol}//${window.location.host}/login_redirect`;
			const pageLink = window.location.pathname + window.location.search;

			const params = this.analytics.analyticsParameters;
			const paramsArr = Object.getOwnPropertyNames(params).map((key) => `${key}=${encodeURIComponent(params[key])}`);
			paramsArr.push(`fsa=${this.location.postalCode}`);
			paramsArr.push(`locale=${this.location.locale}`);
			paramsArr.push(`context=${encodeURIComponent(pageLink)}`);
			if (document.documentElement.getAttribute("native")) {
				paramsArr.push("exp=native");
			}

			/* If we're in distribution, set redirect path to /dist so that beacons can be properly configured after
				* redirect. Including `context` parameter so we can redirect the user to the correct page */
			const path = this.config.RUNTIME === RuntimeConfig.DISTRIBUTION ?
				`/dist?${paramsArr.join("&")}`  :
				window.location.pathname + window.location.search;
			const state = JSON.stringify({
				provider: AuthProvider.GOOGLE,
				path
			});

			const authUrl = `${OAUTH_URL}?response_type=token%20id_token&prompt=select_account&scope=email%20profile&redirect_uri=${redirectUrl}&client_id=${this.config.GOOGLE_CLIENT_ID}&state=${encodeURIComponent(state)}&nonce=${uuid}`;
			window.location.href = authUrl;
		});
	}

	logout = () => {
		return Promise.resolve().then(() => {
			Cookies.remove("flipp-login");
			Cookies.remove("flipp-user");
		});
	}
}
